import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminStats, getAdminCompanies } from '../api/client';
import {
  DollarSign, Building2, Users, CheckCircle,
  Clock, XCircle, Zap, Star, TrendingUp,
} from 'lucide-react';

// ── Reusable stat card ────────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, iconColor, iconBg, accent }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 24,
      border: accent ? `2px solid ${accent}` : '1px solid #E5E7EB',
      boxShadow: accent
        ? `0 4px 20px ${accent}22`
        : '0 1px 3px rgba(0,0,0,0.06)',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#6B7280' }}>{label}</span>
        <div style={{ background: iconBg, borderRadius: 8, padding: 8 }}>
          <Icon size={16} color={iconColor} />
        </div>
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: accent || '#111827', lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: '#9CA3AF' }}>{sub}</div>}
    </div>
  );
}

// ── Plan distribution card ────────────────────────────────────────
function PlanCard({ name, count, mrr, icon: Icon, iconColor, iconBg, totalMrr }) {
  const pct = totalMrr > 0 ? Math.round((mrr / totalMrr) * 100) : 0;
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 20,
      border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ background: iconBg, borderRadius: 8, padding: 8 }}>
          <Icon size={16} color={iconColor} />
        </div>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{name}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#111827' }}>{count}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>companies</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#10B981' }}>${mrr.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>MRR</div>
        </div>
      </div>
      {/* MRR share bar */}
      <div style={{ height: 6, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 3, width: `${pct}%`,
          background: iconColor, transition: 'width 0.5s',
        }} />
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>{pct}% of MRR</div>
    </div>
  );
}

// ── Recent activity row ───────────────────────────────────────────
function RecentRow({ label, last7, last30 }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 0', borderBottom: '1px solid #F3F4F6',
    }}>
      <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>{label}</span>
      <div style={{ display: 'flex', gap: 24 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{last7}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>Last 7 days</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{last30}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>Last 30 days</div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Client-side guard: role + saas admin email (backend still enforces 403)
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const saasEmail = process.env.REACT_APP_SAAS_ADMIN_EMAIL;
    const allowed = user.role === 'admin' && (!saasEmail || user.email === saasEmail);
    if (!allowed) {
      navigate('/dashboard', { replace: true });
      return;
    }
    Promise.all([getAdminStats(), getAdminCompanies()])
      .then(([statsRes, companiesRes]) => {
        setStats(statsRes.data);
        setCompanies(companiesRes.data);
      })
      .catch(err => {
        if (err.response?.status === 403) {
          navigate('/dashboard', { replace: true });
        } else {
          setError('Failed to load admin stats.');
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>Loading admin dashboard...</div>;
  }
  if (error) {
    return <div style={{ textAlign: 'center', padding: 60, color: '#EF4444' }}>{error}</div>;
  }
  if (!stats) return null;

  const totalMrr = stats.mrr;
  const churnRisk = stats.trialing_companies + stats.canceled_subscriptions;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <TrendingUp size={22} color="#2563EB" />
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>
            Admin Revenue Dashboard
          </h1>
        </div>
        <p style={{ color: '#6B7280', fontSize: 14, margin: 0 }}>
          Internal SaaS metrics — visible to platform owner only
        </p>
      </div>

      {/* ── Row 1: Primary KPIs ─────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 16, marginBottom: 20 }}>
        <StatCard
          label="Monthly Recurring Revenue"
          value={`$${totalMrr.toLocaleString()}`}
          sub={`${stats.active_subscriptions} paying companies`}
          icon={DollarSign}
          iconColor="#059669"
          iconBg="#D1FAE5"
          accent="#10B981"
        />
        <StatCard
          label="Active Subscriptions"
          value={stats.active_subscriptions}
          sub="subscription_status = active"
          icon={CheckCircle}
          iconColor="#2563EB"
          iconBg="#DBEAFE"
        />
        <StatCard
          label="Trialing"
          value={stats.trialing_companies}
          sub="within 14-day trial"
          icon={Clock}
          iconColor="#D97706"
          iconBg="#FEF3C7"
        />
        <StatCard
          label="Canceled"
          value={stats.canceled_subscriptions}
          sub="subscription_status = canceled"
          icon={XCircle}
          iconColor="#EF4444"
          iconBg="#FEE2E2"
        />
      </div>

      {/* ── Row 2: Volume ───────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 16, marginBottom: 20 }}>
        <StatCard
          label="Total Companies"
          value={stats.total_companies}
          sub="all plans + trial"
          icon={Building2}
          iconColor="#7C3AED"
          iconBg="#EDE9FE"
        />
        <StatCard
          label="Total Users"
          value={stats.total_users}
          sub="across all companies"
          icon={Users}
          iconColor="#0891B2"
          iconBg="#CFFAFE"
        />
        <StatCard
          label="Avg MRR / Company"
          value={stats.active_subscriptions > 0
            ? `$${Math.round(totalMrr / stats.active_subscriptions)}`
            : '—'}
          sub="active companies only"
          icon={DollarSign}
          iconColor="#6B7280"
          iconBg="#F3F4F6"
        />
        <StatCard
          label="Conversion Rate"
          value={stats.total_companies > 0
            ? `${Math.round((stats.active_subscriptions / stats.total_companies) * 100)}%`
            : '—'}
          sub="trial → paid"
          icon={TrendingUp}
          iconColor="#2563EB"
          iconBg="#DBEAFE"
        />
      </div>

      {/* ── Plan Distribution ────────────────────────────────────── */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: 24,
        border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginBottom: 20,
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginBottom: 16, margin: '0 0 16px 0' }}>
          Plan Distribution &amp; MRR Breakdown
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 16 }}>
          <PlanCard
            name="Starter"
            count={stats.plan_distribution.starter}
            mrr={stats.mrr_by_plan.starter}
            icon={Zap}
            iconColor="#F59E0B"
            iconBg="#FEF3C7"
            totalMrr={totalMrr}
          />
          <PlanCard
            name="Pro"
            count={stats.plan_distribution.pro}
            mrr={stats.mrr_by_plan.pro}
            icon={Star}
            iconColor="#2563EB"
            iconBg="#DBEAFE"
            totalMrr={totalMrr}
          />
          <PlanCard
            name="Business"
            count={stats.plan_distribution.business}
            mrr={stats.mrr_by_plan.business}
            icon={Building2}
            iconColor="#7C3AED"
            iconBg="#EDE9FE"
            totalMrr={totalMrr}
          />
        </div>
      </div>

      {/* ── Recent Activity ──────────────────────────────────────── */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: 24,
        border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#111827', margin: '0 0 4px 0' }}>
          Recent Activity
        </h3>
        <p style={{ fontSize: 12, color: '#9CA3AF', margin: '0 0 16px 0' }}>
          Signups by companies.created_at · Subscriptions by companies.updated_at (active only)
        </p>
        <RecentRow
          label="New Company Signups"
          last7={stats.recent_signups.last_7_days}
          last30={stats.recent_signups.last_30_days}
        />
        <RecentRow
          label="New Paid Subscriptions"
          last7={stats.recent_subscriptions.last_7_days}
          last30={stats.recent_subscriptions.last_30_days}
        />
        <div style={{ paddingTop: 14, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Churn Risk (trial + canceled)</span>
          <span style={{ fontSize: 20, fontWeight: 700, color: churnRisk > 0 ? '#EF4444' : '#10B981' }}>
            {churnRisk}
          </span>
        </div>
      </div>

      {/* ── Company List ─────────────────────────────────────────── */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: 24,
        border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginTop: 20,
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#111827', margin: '0 0 16px 0' }}>
          All Companies ({companies.length})
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {['Company', 'Admin', 'Email', 'Plan', 'Monthly Fee', 'Status', 'Users', 'Signed Up', 'Trial Ends'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {companies.map((c, i) => {
                const statusColor = c.subscription_status === 'active' ? '#10B981' : c.subscription_status === 'trialing' ? '#F59E0B' : '#EF4444';
                return (
                  <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#F9FAFB' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#111827', borderBottom: '1px solid #F3F4F6' }}>{c.name}</td>
                    <td style={{ padding: '10px 12px', color: '#374151', borderBottom: '1px solid #F3F4F6' }}>{c.admin_name || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#374151', borderBottom: '1px solid #F3F4F6' }}>{c.admin_email || c.email}</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6' }}>
                      <span style={{ background: '#EDE9FE', color: '#7C3AED', borderRadius: 6, padding: '2px 8px', fontWeight: 600, textTransform: 'capitalize' }}>{c.plan}</span>
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6', fontWeight: 700, color: c.subscription_status === 'active' ? '#10B981' : '#9CA3AF' }}>
                      {c.subscription_status === 'active' ? `$${c.monthly_price}/mo` : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #F3F4F6' }}>
                      <span style={{ background: `${statusColor}18`, color: statusColor, borderRadius: 6, padding: '2px 8px', fontWeight: 600, textTransform: 'capitalize' }}>{c.subscription_status}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#374151', borderBottom: '1px solid #F3F4F6', textAlign: 'center' }}>{c.user_count}</td>
                    <td style={{ padding: '10px 12px', color: '#6B7280', borderBottom: '1px solid #F3F4F6' }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#6B7280', borderBottom: '1px solid #F3F4F6' }}>{c.trial_end_date ? new Date(c.trial_end_date).toLocaleDateString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {companies.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>No companies yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
